import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * results-list
 * - Keeps your existing layout switching logic
 * - Adds an auto-fix for "collection has products but shows 0 items" caused by stuck filter params/storage
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Auto-fix stuck filters (safe, only triggers when collection has products but current results are 0)
    this.#autoClearStuckFilters();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Auto-clear stuck facet/filter params:
   * Triggers only when:
   * - collection has products (data-all-count > 0)
   * - current results are 0 (data-current-count == 0)
   * - URL contains filter/facet params
   */
  #autoClearStuckFilters() {
    try {
      const allCount = Number(this.getAttribute('data-all-count') || '0');
      const currentCount = Number(this.getAttribute('data-current-count') || '0');

      // Only act when it looks wrong
      if (!(allCount > 0 && currentCount === 0)) return;

      const url = new URL(window.location.href);

      const keys = Array.from(url.searchParams.keys());
      const hasFacetParams = keys.some((k) => {
        return (
          k.startsWith('filter.') ||
          k === 'constraint' ||
          k === 'q' ||
          k === 'type' ||
          k === 'vendor' ||
          k.startsWith('options[') ||
          k === 'sort_by'
        );
      });

      // If no facet params, nothing to clear
      if (!hasFacetParams) return;

      // Prevent infinite loop
      const guardKey = `__cleared_filters__${url.pathname}`;
      if (sessionStorage.getItem(guardKey) === '1') return;
      sessionStorage.setItem(guardKey, '1');

      // Remove a few potentially sticky session keys used by themes (safe)
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        if (
          key.startsWith('facets') ||
          key.startsWith('filters') ||
          key.startsWith('facet') ||
          key.startsWith('collection') ||
          key.startsWith('results')
        ) {
          sessionStorage.removeItem(key);
        }
      }

      // Redirect to clean URL (no params)
      const clean = new URL(url.origin + url.pathname);
      window.location.replace(clean.toString());
    } catch (e) {
      console.warn('[results-list] autoClearStuckFilters skipped:', e);
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}